import { Avatar, createStyles, makeStyles, TextField, Theme } from "@material-ui/core";
import { Column } from "components/Table/Table";
import { DATE_FORMAT_PRINT } from "config/constants";
import { JobTypeColor, PapsServiceColor } from "config/status";
import { COLORS, requestColorPrinter, statusColorPrinter } from "constants/colors";
import useText from "constants/text";
import { checkOrderStatus, checkParcelStatus, setOrderStatus, setParcelStatus } from "domains/CashManagement/data";
import { Hub, Items, Job, JobType, Order, PapsService, Parcel, ParcelStatus, Receiver, Source } from "generated/graphql";
import { useHupAdress } from "hooks/useCurrentUser";
import { OutOfStockIcon, ParcelIcon, ParcelIconTask, TodayIcon } from "icons";
import { truncate } from "lodash";
import moment from "moment";
import Autocomplete from "ui/Autocomplete";
import { ProductIcon } from "ui/Icons";
import StatusInfo from "ui/StatusInfo";
import { currencyFormat, formatPhoneNumber } from "utils";
import { useStyles, useToolTipStyles } from "./style";

interface IRelayToolTip {
  relay?: Hub;
  isThroughRelay: boolean;
}

export const RelayToolTip = ({ relay, isThroughRelay }: IRelayToolTip) => {
  const classes = useToolTipStyles();
  const hubInfos =
    relay?.users &&
    `${relay?.users[0]?.first_name} ${relay?.users[0]?.last_name}-
              ${relay?.openHour?.split(":")[0]}H:${relay?.openHour?.split(":")[1]} -
              ${relay?.endHour?.split(":")[0]}H:${relay?.endHour?.split(":")[1]}`;

  const truncateName = relay?.users && `${relay?.users[0]?.first_name[0]}.${relay?.users[0]?.last_name[0]}`;

  return isThroughRelay ? (
    <div className={classes.badge}>
      RP
      <div className={classes.toolTip}>
        <div className={classes.container}>
          <Avatar className={classes.avatar}>{truncateName}</Avatar>
          <div className={classes.infos}>
            <h3>{relay?.name}</h3>
            <p>
              {truncate(hubInfos, {
                length: 110,
                separator: " ",
              })}
            </p>
            <p>
              {truncate(relay?.location?.address, {
                length: 110,
                separator: " ",
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export const useParcelListStyles = makeStyles((theme: Theme) =>
  createStyles({
    firstLabel: {
      fontWeight: "bold",
      color: "rgb(51 52 54)",
    },
    rescheduling: {
      position: "absolute",
      top: "0px",
      right: "10px",
    },
    items: {
      textAlign: "start",
      position: "relative",
    },
    reasonName: {
      marginTop: "7px",
    },
    statusContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "start",
    },
    icon: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
  })
);

export const useParcelRowColumns = (): Column<Partial<Parcel>>[] => {
  const styles = useParcelListStyles();
  const parcelStatus = [
    ParcelStatus.Successful,
    ParcelStatus.MoneyToCollect,
    ParcelStatus.Returned,
    ParcelStatus.Cancelled,
    ParcelStatus.Failed,
    ParcelStatus.Delivered,
    ParcelStatus.HrOnReturn,
    ParcelStatus.ToReturn,
  ];
  const showReason = (parcel_status: ParcelStatus) => parcelStatus.includes(parcel_status);
  const itemTodisplay = (firstItem: any, secondItem: any, rescheduling?: any) => (
    <div className={styles.items} style={{ color: rescheduling && COLORS.redLight }}>
      <div className={styles.firstLabel} style={{ color: rescheduling && COLORS.redLight }}>
        {firstItem}
      </div>
      <div>{secondItem}</div>
      <div className={styles.rescheduling}>{rescheduling}</div>
    </div>
  );
  const { text } = useText();

  return [
    {
      field: "",
      title: "",
      render: ({ isOutOfStock }) => (
        <div className={styles.icon}> {isOutOfStock ? <OutOfStockIcon width={55} height={25} /> : <ParcelIcon />} </div>
      ),
    },
    {
      field: "parcel_UID",
      title: "Code",
    },
    {
      field: "parcel_order",
      title: `Client`,
      render: ({ parcel_order, service }) => {
        const maxLength = 15;
        let client = '';
        if (service === PapsService.Msg)
          client = `${parcel_order?.order_receiver?.receiver_firstname} ${parcel_order?.order_receiver?.receiver_lastname}`;
        else client = `${parcel_order?.deliveryAddress?.address}`
        return <>{client.length > maxLength ? `${client.substring(0, maxLength - 3)}...` : client}</>;
      },
    },
    {
      field: "parcel_amount_to_collect",
      title: `${text.all.to_collect}`,
      render: ({ parcel_amount_to_collect }) => <> {currencyFormat(parcel_amount_to_collect)} </>,
    },
    {
      field: "parcel_delivery_date",
      title: `${text.all.due_date}`,
      render: ({
        parcel_delivery_date,
        parcel_delivery_slot_start,
        parcel_delivery_slot_end,
        reason_UID,
        parcel_status,
      }) => {
        const date = moment(parcel_delivery_date).format(DATE_FORMAT_PRINT);
        const slot =
          parcel_delivery_slot_start && parcel_delivery_slot_start
            ? `${parcel_delivery_slot_start} - ${parcel_delivery_slot_end}`
            : "N/A";
        const rescheduling =
          (reason_UID === "R-206" && parcel_status === ParcelStatus.AwaitingDelivery && <TodayIcon />) ||
          (reason_UID === "R-206" && parcel_status === ParcelStatus.ToBeAssigned && <TodayIcon />);
        return itemTodisplay(date, slot, rescheduling);
      },
    },
    {
      field: "parcel_status",
      title: `${text.all.status}`,
      render: ({ parcel_status, parcel_reason, parcel_attempts_real, parcel_attempts }) => {
        const parcelStatus = checkParcelStatus.includes(parcel_status) ? setParcelStatus[parcel_status] : parcel_status;

        return (
          <div className={styles.statusContainer}>
            <StatusInfo
              width="127px"
              color={statusColorPrinter({ status: parcelStatus, type: "text" })}
              background={statusColorPrinter({ status: parcelStatus, type: "bg" })}
              border={statusColorPrinter({ status: parcelStatus, type: "border" })}
              status={parcelStatus?.split("_")?.join(" ")}
            />
            {(showReason(parcel_status || ParcelStatus.Dropped) ||
              parcel_attempts !== 0 ||
              parcel_attempts_real !== 0) && (
                <div className={styles.reasonName}>{parcel_reason?.reason_name || " "}</div>
              )}
          </div>
        );
      },
    },
    {
      field: "parcel_attempts",
      title: `${text.all.attempts}`,
    },
    {
      field: "service",
      title: "Services",
      render: ({ service }) => (
        <p style={{
          color: PapsServiceColor[service],
          display: "flex",
          justifyContent: "start",
          justifyItems: "start",
          fontWeight: "bold",
        }}>{service}</p>
      ),
    },
    {
      field: "platform",
      title: `${text.all.platform}`,
      render: ({ parcel_created_by, isFromAPI }) => (
        <>{isFromAPI ? "API" : parcel_created_by?.source === Source.Client ? "MyPaps" : "Paps Ops"}</>
      ),
    },
    {
      field: "",
      title: "",
      render: ({ relay, isThroughRelay }) => {
        return <RelayToolTip relay={relay} isThroughRelay={isThroughRelay} />;
      },
    },
  ];
};

export const useOrdersColums = (): Column<Order>[] => {
  const text = useText();

  return [
    {
      field: "order_UID",
      title: "Code",
    },
    {
      field: "order_receiver",
      title: "Client",
      render: ({ order_receiver }) => {
        const maxLength = 15;
        const fullname = `${order_receiver?.receiver_firstname} ${order_receiver?.receiver_lastname}`;
        return <>{fullname.length > maxLength ? `${fullname.substring(0, maxLength - 3)}...` : fullname}</>;
      },
    },
    {
      field: "order_receiver",
      title: "Telephone",
      render: ({ order_receiver }) => <>{order_receiver?.receiver_phone_number}</>,
    },
    {
      field: "order_receiver",
      title: "Adresse",
      render: ({ order_receiver }) => <>{order_receiver?.receiver_address.address}</>,
    },
    {
      field: "order_status",
      title: "Status",
      render: ({ order_status }) => {
        const orderStatus = checkOrderStatus.includes(order_status) ? setOrderStatus[order_status] : order_status;
        return (
          <StatusInfo
            width="127px"
            color={statusColorPrinter({ status: orderStatus, type: "text" })}
            background={statusColorPrinter({ status: orderStatus, type: "bg" })}
            border={statusColorPrinter({ status: orderStatus, type: "border" })}
            status={orderStatus?.split("_")?.join(" ")}
          />
        );
      },
    },
    {
      field: "order_parcel",
      title: "Parcels",
      render: ({ order_parcels, order_status }) => (
        <>
          <StatusInfo width="30px" color={COLORS.white} background={COLORS.orange} status={order_parcels?.length} />
        </>
      ),
    },
    {
      field: "",
      title: "",
      render: ({ relay, isThroughRelay }) => {
        return (
          // eslint-disable-next-line react/react-in-jsx-scope
          <RelayToolTip relay={relay} isThroughRelay={isThroughRelay} />
        );
      },
    },
  ];
};

export const useDefaultRowParcel = (): Column<Partial<Parcel>>[] => {
  const { text } = useText();
  const style = useStyles();

  return [
    {
      field: "product",
      title: `${text.requestQuote.product}`,
      render: ({ parcel_description }) => (
        <div className={style.divStyled}>
          <ProductIcon /> &nbsp;&nbsp;{parcel_description}
        </div>
      ),
    },
    {
      field: "parcel_UID",
      title: `${text.all.reference}`,
    },
    {
      field: "parcel_size",
      title: `${text.all.size}`,
    },

    {
      field: "parcel_amount_to_collect",
      title: `${text.all.price}`,
      render: ({ parcel_amount_to_collect }) => <> {currencyFormat(parcel_amount_to_collect)} </>,
    },
  ];
};

export const useJobOrderPreviousRowParcel = (): Column<Partial<Parcel>>[] => {
  const { text } = useText();
  return [
    {
      field: "parcel_UID",
      title: `Code`,
    },
    {
      field: "parcel_description",
      title: `${text.all.description}`,
    },
    {
      field: "parcel_size",
      title: `${text.all.size}`,
    },

    {
      field: "parcel_value",
      title: `${text.all.value}`,
      render: ({ parcel_value }) => <> {currencyFormat(parcel_value)} </>,
    },
    {
      field: "parcel_amount_to_collect",
      title: `${text.all.amount_to_collect}`,
      render: ({ parcel_amount_to_collect }) => <> {currencyFormat(parcel_amount_to_collect)} </>,
    },
    {
      field: "parcel_client_reference",
      title: `${text.all.reference}`,
      render: ({ parcel_client_reference }) => <>{parcel_client_reference || "N/A"}</>,
    },
  ];
};

export const useJobColumns = (): Column<Partial<Job>>[] => {
  const { text } = useText();
  const defaultAdress = useHupAdress();
  return [
    {
      field: "job_type",
      title: `${text.all.task_type}`,
      render: ({ job_type }) => (
        <div
          style={{
            color: JobTypeColor[job_type],
            display: "flex",
            justifyContent: "start",
            justifyItems: "start",
          }}
        >
          <div
            style={{
              marginTop: "auto",
              marginBottom: "auto",
              height: 35,
              width: 35,
              borderRadius: 12,
              background: job_type === JobType.Pickup ? "#e3f8fa" : "#faecca",
              display: "flex",
              justifyContent: "center",
              justifyItems: "center",
            }}
          >
            <div
              style={{
                marginTop: "auto",
                marginBottom: "auto",
                display: "flex",
                justifyContent: "center",
                justifyItems: "center",
              }}
            >
              {job_type === JobType.Pickup ? <ProductIcon color="#FFF" /> : <ProductIcon color="#faecca" />}
            </div>
          </div>
          <div
            style={{
              marginTop: "auto",
              marginBottom: "auto",
              marginLeft: 15,
              textTransform: "capitalize",
            }}
          >
            {job_type === JobType.Pickup ? `${text.all.pickup}` : `${text.all.delivery}`}
          </div>
        </div>
      ),
    },
    {
      field: "job_address",
      title: `${text.all.address}`,
      render: ({ job_address, job_client, job_type }) => (
        <>{job_type === JobType.Pickup ? job_address?.address ?? job_client?.client_address?.address : defaultAdress}</>
      ),
    },
    {
      field: "job_created_at",
      title: `${text.all.creation_date}`,
      render: ({ job_created_at }) => <> {moment(job_created_at).format(DATE_FORMAT_PRINT)} </>,
    },
    {
      field: "job_date",
      title: `${text.all.execution_date}`,
      render: ({ job_date }) => <> {moment(job_date).format(DATE_FORMAT_PRINT)} </>,
    },
  ];
};
interface RelaysPointProps {
  selectId?: string;
  relayPointCity?: string[];
  cityValue?: string;
  handleSelect?: (id: string) => void;
  loading?: boolean;
  relays?: string[];
  handleOnInputRelaypointChange?: (value: Hub, receiverId: string) => void;
}
export const RelaysPoint = (
  {
    selectId,
    relayPointCity,
    cityValue,
    handleSelect,
    loading,
    relays,
    handleOnInputRelaypointChange,
  }: RelaysPointProps,
  _id: string
) => {
  const { text } = useText();
  const styles = useStyles();
  const isDisabled = selectId.includes(_id);
  const disabledInputRelay = () => !isDisabled || loading || !cityValue;

  const handleInputRelaypointChange = (event: any, newInputValue: Hub) => {
    handleOnInputRelaypointChange(newInputValue, _id);
  };
  const getLabel = (title = "") => title;

  return (
    <div className={styles.relayContent}>
      <div className={styles.fieldAutocompleteRelay}>
        <Autocomplete
          disabled={!isDisabled}
          options={relayPointCity}
          onChange={handleSelect}
          label={text.all.city}
          name={`city${_id}`}
          getOptionLabel={(option: any) => option}
          renderInput={(params: any) => <TextField {...params} variant="outlined" />}
        />
      </div>
      <div className={styles.fieldAutocompleteRelay}>
        <Autocomplete
          disabled={disabledInputRelay()}
          options={relays}
          onChange={handleInputRelaypointChange}
          label={text.all.relay_point}
          name={`relay${_id}`}
          getOptionLabel={({ name }: any) => getLabel(name)}
          renderInput={(params: any) => <TextField {...params} variant="outlined" />}
        />
      </div>
    </div>
  );
};

export const useReceiversColumns = (text: any, RP?: any): Column<Partial<Receiver>>[] => {
  return [
    {
      field: "receiver_firstname",
      title: `${text.all.first_name}`,
    },
    {
      field: "receiver_lastname",
      title: `${text.all.last_name}`,
    },
    {
      field: "receiver_phone_number",
      title: `${text.all.phone_number}`,
      render: ({ receiver_phone_number }) => <> {formatPhoneNumber(receiver_phone_number)} </>,
    },
    {
      field: "receiver_address",
      title: `${text.all.address}`,
      render: ({ receiver_address }) => <> {`${receiver_address?.address.substring(0, 32)}...`}</>,
    },
    {
      field: "Reception_at_Relay_Point",
      title: `${text.all.reception_at_Relay_Point_field}`,
      render: ({ _id }) => RelaysPoint(RP, _id),
    },
  ];
};
export const useItemRowTaskColumns = (): Column<Partial<Items>>[] => {
  const { text } = useText();
  return [
    {
      field: "description",
      title: `${text.all.description}`,
    },
    {
      field: "quantity",
      title: `${text.all.quantity}`,
    },
  ];
};

export const useParcelRowTaskColumns = (): Column<Partial<Parcel>>[] => {
  const styles = useParcelListStyles();
  const { text } = useText();

  return [
    {
      field: "",
      title: "",
      render: ({ }) => <div className={styles.icon}> {<ParcelIconTask />} </div>,
    },
    {
      field: "parcel_UID",
      title: "Parcel",
    },
    {
      field: "parcel_order",
      title: `Receiver`,
      render: ({ parcel_order }) => {
        const maxLength = 15;
        const fullname = `${parcel_order?.order_receiver?.receiver_firstname} ${parcel_order?.order_receiver?.receiver_lastname}`;
        return <>{fullname.length > maxLength ? `${fullname.substring(0, maxLength - 3)}...` : fullname}</>;
      },
    },
    {
      field: "parcel_amount_to_collect",
      title: `${text.all.to_collect}`,
      render: ({ parcel_amount_to_collect }) => <> {currencyFormat(parcel_amount_to_collect)} </>,
    },
    {
      field: "parcel_value",
      title: `${text.all.value}`,
      render: ({ parcel_value }) => <> {parcel_value} </>,
    },
    {
      field: "parcel_delivery_date",
      title: `${text.all.due_date}`,
      render: ({ parcel_delivery_date }) => <>{moment(parcel_delivery_date).format(DATE_FORMAT_PRINT)}</>,
    },
    {
      field: "parcel_status",
      title: `${text.all.status}`,
      render: ({ parcel_status }) => {
        const parcelStatus = checkParcelStatus.includes(parcel_status) ? setParcelStatus[parcel_status] : parcel_status;

        return (
          <div className={styles.statusContainer}>
            <StatusInfo width="127px" color="#fff" background="#6ac6d1" status={parcelStatus?.split("_")?.join(" ")} />
          </div>
        );
      },
    },
    {
      field: "parcel_attempts",
      title: `${text.all.attempts}`,
      render: ({ parcel_attempts }) => <> {parcel_attempts} </>,
    },
  ];
};

enum HTTP_STATUS_CODES {
  OK = 200,
  SUCCESS = 201,
}

export default HTTP_STATUS_CODES;
